import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
    setSelectedAssignedProjectId,
    setSelectedAssignedFolderId,
} from "@store/slices/translatorAdminSlice";
import { fetchAllInProgressProjects, fetchInProgressFolders, fetchInProgressFiles } from "@store/thunks/translationThunk";
import { ROLE_ROUTES } from "@utils/constants";

const TranslatorSidebar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { assignedProjects, assignedFolders, assignedFiles, selectedAssignedProjectId, selectedAssignedFolderId } = useSelector(
        (store) => store.translatorAdmin
    );

    const { role } = useSelector((store) => store.auth);

    const destinationRoute = ROLE_ROUTES[role] || ROLE_ROUTES.DEFAULT;

    const [isFolderLoading, setIsFolderLoading] = useState({}); // Track loading state per projectId

    useEffect(() => {
        dispatch(fetchAllInProgressProjects());
    }, [dispatch]);

    useEffect(() => {
        if (location.pathname === `${destinationRoute}`) {
            dispatch(setSelectedAssignedProjectId(null));
        }
    }, [location.pathname, destinationRoute]);

    const handleProjectClick = async (projectId) => {
        console.log("Project ID clicked:", projectId);
        if (selectedAssignedProjectId === projectId) {
            dispatch(setSelectedAssignedProjectId(projectId));
        } else {
            dispatch(setSelectedAssignedProjectId(projectId));
            if (!assignedFolders[projectId]) {
                setIsFolderLoading((prev) => ({ ...prev, [projectId]: true })); // Set loading state
                try {
                    await dispatch(fetchInProgressFolders(projectId)).unwrap();
                } catch (error) {
                    console.error("Error fetching folders:", error);
                } finally {
                    setIsFolderLoading((prev) => ({ ...prev, [projectId]: false })); // Clear loading state
                }
            }
        }
        navigate(`${destinationRoute}/folders`);
    };

    const handleFolderClick = async (folderId) => {
        if (selectedAssignedFolderId === folderId) {
            dispatch(setSelectedAssignedFolderId(folderId));
        } else {
            dispatch(setSelectedAssignedFolderId(folderId));
            if (!assignedFiles[folderId]) {
                await dispatch(fetchInProgressFiles(folderId));
            }
        }
        navigate(`${destinationRoute}/folders/files`);
    };

    return (
        <div className="bg-white border-r border-gray-200 shadow-sm h-screen overflow-y-auto w-full mt-12">
            <div>
                <h3 className="font-bold text-lg mb-4">Translator Admin Dashboard</h3>
                {assignedProjects && assignedProjects.length > 0 ? (
                    [...assignedProjects]
                        .filter((project) => project.status !== "DELETED")
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Sort descending by date
                        .map((project) => (
                            <div key={project.projectId} className="mb-3">
                                {/* Project Level */}
                                <div
                                    className={`cursor-pointer p-2 rounded-lg ${selectedAssignedProjectId === project.projectId
                                            ? "bg-blue-600 text-white"
                                            : "bg-blue-500 text-white hover:bg-blue-600"
                                        }`}
                                    onClick={() => handleProjectClick(project.projectId)}
                                >
                                    {project.projectName}
                                </div>

                                {/* Folders Level */}
                                {selectedAssignedProjectId === project.projectId && (
                                    <div className="ml-4 mt-2">
                                        {isFolderLoading[project.projectId] ? (
                                            <div className="p-2 text-gray-500">Loading folders...</div>
                                        ) : (
                                            [...(assignedFolders[project.projectId] || [])]
                                                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Sort folders
                                                .map((folder) => (
                                                    <div
                                                        key={folder.folderId}
                                                        className={`cursor-pointer p-2 rounded ${selectedAssignedFolderId === folder.folderId
                                                                ? "bg-gray-200"
                                                                : "bg-gray-100 hover:bg-gray-200"
                                                            }`}
                                                        onClick={() => handleFolderClick(folder.folderId)}
                                                    >
                                                        {folder.folderName}
                                                    </div>
                                                ))
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                ) : (
                    <div className="text-gray-500">No projects available</div>
                )}
            </div>
        </div>
    );
};

export default TranslatorSidebar;